import {makeAutoObservable, observable, runInAction} from 'mobx';
import type {StudioVersion} from '../../../../../data/api/StudioApi';
import StudioRepository from '../../../../../data/repositories/StudioRepository';
import type {StudioResponse} from '../../../../../data/repositories/StudioRepository';
import type {ReelMotionActions} from '../../../components/storyboardActions';
import {
  cannotCheckCredits,
  creditsNotSettled,
  hasCreditHold,
} from '../../../creditCheck';
import {
  ApiError,
  errorMessage,
  GENERIC_ERROR,
  isCreditCheckPending,
  isInsufficientCredits,
} from '../../../errors';
import {MutationScope, scopedIdentity} from '../../../store/MutationScope';
import {publicationState} from '../../../store/PublicationStore';
import type PublicationStore from '../../../store/PublicationStore';
import type StudioJobStore from '../../../store/StudioJobStore';
import type BrandStore from '../../../store/BrandStore';
import {studioRequest} from '../../../store/studioRequests';
import type {JobView, Publication} from '../../../types';
import {retryState, ShareKind} from '../reviewModel';
import {reviewText} from '../reviewText';

/** The platform's published group of a shared render (MicroreelsListStore's lookup). */
export type FindPublishedGroup = (
  organizationId: string,
  groupId: string,
) => Promise<{id?: string} | undefined>;

/**
 * The picker that is open. A course picker for a render not shared yet has no group: it shares
 * the render once the author confirms a course (see `shareToCourse`).
 */
export interface ShareModal {
  kind: ShareKind;
  publishedCourseId: string | null;
}

/**
 * The videos page's actions on its job: generating again, renaming, canceling, trying another
 * look for a microreel, and sharing with learners or a course. The job itself is the
 * registered StudioJobStore's and whether it is shared the PublicationStore's; this store
 * holds what the page's buttons are doing and why the last one failed. Each answer is shown
 * only while the page still shows the job it was for. Registered in storesProvider as
 * `studioReviewStore`.
 */
export default class ReviewStore {
  /** The last action was refused for too few AI credits: its alert links to adding them. */
  private creditRefusal = false;
  generating = false;
  canceling = false;
  /** Studio is settling the AI credits a stopped generation holds (see `checkCredits`). */
  checkingCredits = false;
  /** The share button opening its picker (sharing the render first when needed). */
  opening: ShareKind | null = null;
  modal: ShareModal | null = null;
  /**
   * The microreels whose look or sound changed on this page since it last generated: their
   * phone says the new look is ready to preview.
   */
  restyled: ReadonlySet<string> = new Set();
  organizationId: string | undefined = undefined;
  jobId = '';

  private repository = new StudioRepository();
  private scope = new MutationScope();
  private openings = 0;
  /** Why the last action failed, written for authors; see `actionError`. */
  private failure: string | null = null;
  /** The last credit check left the credits unsettled; see `actionError`. */
  private unsettled = false;

  constructor(
    private readonly jobs: StudioJobStore,
    private readonly publications: PublicationStore,
    private readonly brands: BrandStore,
  ) {
    makeAutoObservable<
      ReviewStore,
      'repository' | 'scope' | 'openings' | 'jobs' | 'publications' | 'brands'
    >(this, {
      modal: observable.ref,
      restyled: observable.ref,
      repository: false,
      scope: false,
      openings: false,
      jobs: false,
      publications: false,
      brands: false,
    });
  }

  /** The job the page opened, once the job store shows it. */
  get view(): JobView | null {
    return this.jobs.identity ===
      scopedIdentity(this.organizationId, this.jobId)
      ? this.jobs.view
      : null;
  }

  /**
   * Why the last action failed, written for authors. That a credit check left the credits
   * unsettled holds only while they still wait: once a read of the job shows them settled,
   * it goes.
   */
  get actionError(): string | null {
    if (!this.unsettled)
      return this.creditRefusal &&
        this.view?.billing?.enforceAiCredits === false
        ? GENERIC_ERROR
        : this.failure;
    return hasCreditHold(this.view) ? creditsNotSettled(this.view) : null;
  }

  get needsCredits() {
    return this.creditRefusal && this.view?.billing?.enforceAiCredits !== false;
  }

  /** Whether the job's current render is shared; see `publicationState`. */
  get sharing() {
    return publicationState(this.publications, this.jobId, this.view);
  }

  get savingBrand() {
    return this.brands.isSavingFor(this.organizationId, this.jobId);
  }

  /**
   * The page shows this job: what the last one was doing is forgotten, even when the page
   * opens the same job again. Returns the function that closes this opening, for an effect's
   * cleanup.
   */
  open(organizationId: string | undefined, id: string) {
    this.organizationId = organizationId;
    this.jobId = id;
    this.reset();
    const opening = ++this.openings;
    // Each opening is its own scope, so an answer to an earlier visit never lands on this one.
    this.scope.setIdentity(`${scopedIdentity(organizationId, id)}#${opening}`);
    this.scope.activate();
    return () => {
      if (this.openings === opening) this.close();
    };
  }

  /** The page went away: answers still on their way are ignored. */
  close() {
    this.scope.deactivate();
    this.reset();
  }

  /** Generates the microreels again (after a failure or a storyboard change). */
  async generate() {
    const view = this.view;
    if (
      !view ||
      this.generating ||
      this.savingBrand ||
      this.checkingCredits ||
      view.running ||
      retryState(view).retryBlocked
    )
      return;
    // Rendering may fill missing prompts and change the storyboard's version before it
    // answers, so the answer belongs to the job rather than to one version of it.
    const current = this.scope.begin();
    const {organizationId, jobId: id} = this;
    this.generating = true;
    try {
      await studioRequest(organizationId, org =>
        this.repository.render(
          org,
          id,
          view.job.storeVersion,
          view.job.storyboard?.versionHash,
        ),
      );
      if (current()) {
        this.failAction(null);
        // The generation shows every new look made here.
        runInAction(() => {
          this.restyled = new Set();
        });
        this.jobs.reload();
      }
    } catch (e) {
      if (current()) this.failAction(e);
    } finally {
      if (current())
        runInAction(() => {
          this.generating = false;
        });
    }
  }

  /**
   * Asks Studio to settle the AI credits a stopped generation holds, e.g. once the workspace
   * has added some, then reads the job again: settled credits let it generate again. When
   * they still cannot settle (or the server cannot check), generating stays locked and the
   * alert says so.
   */
  async checkCredits() {
    const view = this.view;
    const {organizationId, jobId: id} = this;
    if (
      !view ||
      this.checkingCredits ||
      this.generating ||
      view.running ||
      view.job.status === 'rendering' ||
      !hasCreditHold(view)
    )
      return;
    const current = this.scope.begin();
    this.checkingCredits = true;
    this.failAction(null);
    try {
      const {settled} = await studioRequest(organizationId, org =>
        this.repository.creditCheck(org, id),
      ).catch(e => {
        if (cannotCheckCredits(e)) return {settled: false};
        throw e;
      });
      if (!current()) return;
      await this.jobs.reload();
      // Settled meanwhile by the background pass, or by work that started: nothing to say.
      const stillLocked = hasCreditHold(this.view) && !this.view?.running;
      if (current() && !settled && stillLocked)
        runInAction(() => {
          this.unsettled = true;
        });
    } catch (e) {
      if (current()) this.failAction(e);
    } finally {
      if (current())
        runInAction(() => {
          this.checkingCredits = false;
        });
    }
  }

  /** The microreel's look or sound changed on this page, and no generation has shown it yet. */
  lookChanged(reelId: string | undefined) {
    return !!reelId && this.restyled.has(reelId);
  }

  /**
   * A microreel's look and sound changes, against the version shown. Each resolves to the job
   * the server returns, which the panel that sent it shows; a failure rejects, for it to say.
   */
  motionActions(reelId: string): ReelMotionActions {
    return {
      tryAnotherLook: () =>
        this.restyle(reelId, (organizationId, id, version) =>
          this.repository.tryAnotherLook(organizationId, id, reelId, version),
        ),
      updateMotion: change =>
        this.restyle(reelId, (organizationId, id, version) =>
          this.repository.updateReelMotion(
            organizationId,
            id,
            reelId,
            change,
            version,
          ),
        ),
    };
  }

  /** Renames the microreels; a failure rejects, for the rename field to show. */
  async renameJob(title: string) {
    await this.jobs.mutate(({organizationId, id, storeVersion}) =>
      studioRequest(organizationId, org =>
        this.repository.renameJob(org, id, title, storeVersion),
      ),
    );
  }

  /** Stops the generation; the job store reads the job again once it has. */
  async cancel() {
    const view = this.view;
    if (!view || this.generating || this.canceling) return;
    const current = this.scope.begin();
    this.canceling = true;
    try {
      await this.jobs.cancel(view.job.storeVersion);
      if (current()) this.failAction(null);
    } catch (e) {
      if (current()) this.failAction(e);
    } finally {
      if (current())
        runInAction(() => {
          this.canceling = false;
        });
    }
  }

  /**
   * Opens a share picker. Sharing with learners shares the render first. Adding to a course
   * shares it only once the author confirms a course, so opening the picker and closing it
   * shares nothing.
   */
  async openShare(kind: ShareKind, findGroup?: FindPublishedGroup) {
    const sharing = this.sharing;
    if (!this.view || this.opening || sharing.publishing || this.savingBrand)
      return;
    const current = this.scope.begin();
    this.opening = kind;
    this.failAction(null);
    try {
      if (kind === 'course' && !sharing.published) {
        this.modal = {kind, publishedCourseId: null};
        return;
      }
      const publication = sharing.published
        ? sharing.publication
        : await this.publish();
      if (!current() || !publication) return;
      const group = await this.publishedGroup(publication, findGroup);
      if (!current()) return;
      runInAction(() => {
        this.modal = {kind, publishedCourseId: group};
      });
    } catch (e) {
      if (current()) this.failAction(e);
    } finally {
      if (current())
        runInAction(() => {
          this.opening = null;
        });
    }
  }

  /**
   * Shares the render for the course picker once the author confirms a course, and resolves
   * to the published group the picker adds to the course.
   */
  async shareToCourse(findGroup?: FindPublishedGroup) {
    const publication = await this.publish();
    if (!publication) throw new Error(reviewText.shareFailed);
    return this.publishedGroup(publication, findGroup);
  }

  closeModal() {
    this.modal = null;
  }

  /** Reads the publication and the job again after the publication could not be read. */
  retrySharing() {
    this.publications.retry(() => this.jobs.reload());
  }

  /**
   * Says why an action failed, for authors; null clears the last failure. A refusal for a
   * pending credit check reads the job again instead: its own alert says why and what unlocks
   * it (as StoryboardStore's `fail` does).
   */
  private failAction(error: unknown) {
    const creditCheck = isCreditCheckPending(error);
    const enforceAiCredits = this.view?.billing?.enforceAiCredits !== false;
    this.failure =
      error === null || creditCheck
        ? null
        : errorMessage(error, enforceAiCredits);
    this.creditRefusal = isInsufficientCredits(error);
    this.unsettled = false;
    if (creditCheck) void this.jobs.reload();
  }

  private async restyle(
    reelId: string,
    request: (
      organizationId: string,
      id: string,
      version: StudioVersion,
    ) => Promise<StudioResponse<JobView>>,
  ): Promise<JobView> {
    const view = this.view;
    if (!view) throw new ApiError(GENERIC_ERROR, 0);
    const current = this.scope.begin();
    const {organizationId, jobId: id} = this;
    const result = await studioRequest(organizationId, org =>
      request(org, id, view.job.storeVersion),
    );
    if (current())
      runInAction(() => {
        this.restyled = new Set(this.restyled).add(reelId);
      });
    return result;
  }

  private publish() {
    if (this.savingBrand) return Promise.resolve(null);
    return this.publications.publish(() => this.jobs.reload());
  }

  /** The published platform group of a shared render, which learners and courses are given. */
  private async publishedGroup(
    publication: Publication,
    findGroup?: FindPublishedGroup,
  ) {
    const groupId = publication.courseId || this.sharing.courseId;
    const organizationId = this.organizationId;
    const group =
      organizationId && groupId && findGroup
        ? await findGroup(organizationId, groupId)
        : undefined;
    if (!group?.id) throw new Error(reviewText.learnerListNotReady);
    return group.id;
  }

  private reset() {
    this.failure = null;
    this.unsettled = false;
    this.creditRefusal = false;
    this.generating = false;
    this.canceling = false;
    this.checkingCredits = false;
    this.opening = null;
    this.modal = null;
    this.restyled = new Set();
  }
}
